import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Select, Spin } from "antd";
import AuthService from "../../base/services/authentication.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { deriveCountryCode } from "../../pages/auth/register/registrationDefaults";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { fieldPathToName, toProfileFormValues, toProfilePayload } from "./billingProfile";
import { getCountryOptions } from "./countries";
import "./index.scss";

// Who invoices are made out to: the billing profile, saved to the organization's
// Stripe customer. Stripe needs the address to raise a valid invoice, so it is
// saved before a card is — the card form (CardSetup) opens on this whenever the
// profile is not complete yet.
//
// Only the address is asked for. The name and email the API also requires are
// carried in hidden fields: what was saved, or the defaults below for a new
// profile. No tax number is taken, so saving removes one saved before.
//
// The fields are the API's; a validation failure the server pins to one of them
// (`field`) is shown under that field, and anything else over the button.

// "There is no profile yet", which the form answers by starting an empty one.
const NO_PROFILE_YET = new Set([BillingCode.NO_STRIPE_CUSTOMER, BillingCode.BILLING_PROFILE_INCOMPLETE]);

const required = (message) => [{ required: true, whitespace: true, message }];

// What an empty profile starts from: the organization's name, the signed-in
// person's email, and the country their phone number was dialled from. The
// name and email aren't shown, so a new profile's invoices go out under them.
const profileDefaults = () => {
   const user = AuthService.getUser() ?? {};

   return {
      name: LocalStorageService.getItem("organizationName") || user.fullName || "",
      email: user.email || "",
      country: deriveCountryCode(user.phoneNumber)
   };
};

function BillingProfileForm({ organizationId, onSaved, onCancel, submitLabel = "Save billing details" }) {
   const [form] = Form.useForm();
   const [saved, setSaved] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [isSaving, setIsSaving] = useState(false);
   const [formError, setFormError] = useState(null);
   const countryOptions = useMemo(getCountryOptions, []);

   useEffect(() => {
      let isCurrent = true;
      setIsLoading(true);

      billingApi
         .getBillingProfile(organizationId)
         .then((profile) => {
            if (!isCurrent) return;
            setSaved(profile);
            form.setFieldsValue(toProfileFormValues(profile, profileDefaults()));
         })
         .catch((failure) => {
            if (!isCurrent) return;
            form.setFieldsValue(toProfileFormValues(null, profileDefaults()));
            if (!NO_PROFILE_YET.has(failure.code) && failure.statusCode !== 404) {
               setFormError(describeBillingError(failure));
            }
         })
         .finally(() => {
            if (isCurrent) setIsLoading(false);
         });

      return () => {
         isCurrent = false;
      };
   }, [organizationId, form]);

   const handleFinish = async (values) => {
      if (isSaving) return;

      setIsSaving(true);
      setFormError(null);

      try {
         const profile = await billingApi.saveBillingProfile(
            toProfilePayload(values, organizationId, saved)
         );
         setSaved(profile);
         onSaved?.(profile);
      } catch (failure) {
         const name = fieldPathToName(failure.field);

         if (name) {
            form.setFields([{ name, errors: [describeBillingError(failure)] }]);
            form.scrollToField(name);
         } else {
            setFormError(describeBillingError(failure));
         }
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <div className="billing-form">
         <Spin spinning={isLoading}>
            <Form
               form={form}
               layout="vertical"
               requiredMark={false}
               onFinish={handleFinish}
               scrollToFirstError
            >
               <Form.Item name="name" hidden>
                  <Input />
               </Form.Item>
               <Form.Item name="email" hidden>
                  <Input />
               </Form.Item>

               <Form.Item
                  name={["address", "line1"]}
                  label="Address"
                  rules={required("Please enter the billing address")}
               >
                  <Input size="large" placeholder="Street and number" autoComplete="address-line1" />
               </Form.Item>

               <Form.Item name={["address", "line2"]} label="Address line 2 (optional)">
                  <Input size="large" placeholder="Apartment, suite, floor" autoComplete="address-line2" />
               </Form.Item>

               <div className="billing-form__row">
                  <Form.Item
                     name={["address", "city"]}
                     label="City"
                     rules={required("Please enter the city")}
                  >
                     <Input size="large" placeholder="City" autoComplete="address-level2" />
                  </Form.Item>
                  {/* Only some countries have one, and none is refused for want of it. */}
                  <Form.Item name={["address", "state"]} label="State / Province (optional)">
                     <Input size="large" placeholder="State / Province" autoComplete="address-level1" />
                  </Form.Item>
               </div>

               <div className="billing-form__row">
                  <Form.Item
                     name={["address", "postalCode"]}
                     label="ZIP / Postal code"
                     rules={required("Please enter the ZIP or postal code")}
                  >
                     <Input size="large" placeholder="ZIP / Postal code" autoComplete="postal-code" />
                  </Form.Item>
                  <Form.Item
                     name={["address", "country"]}
                     label="Country"
                     rules={[{ required: true, message: "Please select the country" }]}
                  >
                     <Select
                        size="large"
                        showSearch
                        placeholder="Select the country"
                        options={countryOptions}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                           option.label.toLowerCase().includes(input.toLowerCase())
                        }
                     />
                  </Form.Item>
               </div>

               {formError && (
                  <div className="billing-form__message" role="alert">
                     {formError}
                  </div>
               )}

               <div className="billing-actions">
                  {onCancel && (
                     <Button className="defaultButton" onClick={onCancel} disabled={isSaving}>
                        Cancel
                     </Button>
                  )}
                  <Button
                     htmlType="submit"
                     className="kapSurveyPrimaryButton"
                     loading={isSaving}
                     disabled={isLoading}
                  >
                     {submitLabel}
                  </Button>
               </div>
            </Form>
         </Spin>
      </div>
   );
}

export default BillingProfileForm;
